import { isRecord } from '@/lib/persist';
import { isValidHex, normalizeHex } from '@/lib/studio-color';
import { getRelativeLuminance } from '@/lib/accessibility';
import {
  completeTheme,
  createDefaultPlayground,
  MAX_THEME_SNAPSHOTS,
  THEME_ROLES,
  themesFromPalette,
} from '@/lib/playground-theme';
import { SOURCE_LIMIT } from '@/data/playground-starter';
import type {
  PlaygroundDesign,
  PlaygroundRoles,
  PlaygroundState,
  ThemeRoles,
  ThemeSnapshot,
} from '@/types/studio';

const choice = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  typeof value === 'string' && allowed.includes(value as T) ? (value as T) : fallback;
const number = (value: unknown, fallback: number, min: number, max: number) =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.max(min, Math.min(max, value))
    : fallback;
const text = (value: unknown, fallback: string, max: number) =>
  typeof value === 'string' ? value.slice(0, max) : fallback;

function parseRoles(value: unknown, defaults: PlaygroundRoles): PlaygroundRoles {
  const data = isRecord(value) ? value : {};
  return Object.fromEntries(
    THEME_ROLES.map(({ id }) => [
      id,
      typeof data[id] === 'string' && isValidHex(data[id]) ? normalizeHex(data[id]) : defaults[id],
    ]),
  ) as PlaygroundRoles;
}

function parseDesign(value: Record<string, unknown>, defaults: PlaygroundDesign): PlaygroundDesign {
  const themes = isRecord(value.themes) ? value.themes : {};
  return {
    themes: {
      light: parseRoles(themes.light, defaults.themes.light),
      dark: parseRoles(themes.dark, defaults.themes.dark),
    },
    radius: number(value.radius, defaults.radius, 0, 32),
    font: choice(value.font, ['sans', 'serif', 'mono'], defaults.font),
    fontScale: number(value.fontScale, defaults.fontScale, 85, 125),
    density: choice(value.density, ['comfortable', 'compact'], defaults.density),
  };
}

/** Upgrade the previous single-theme document once, then use the current shape everywhere. */
export function migrateLegacyPlayground(value: Record<string, unknown>): Record<string, unknown> {
  if (!isRecord(value.roles) || 'themes' in value) return value;
  const defaults = createDefaultPlayground();
  const legacyRoles = value.roles;
  const roles = Object.fromEntries(
    ['background', 'surface', 'text', 'primary', 'secondary', 'accent'].map((id) => [
      id,
      legacyRoles[id],
    ]),
  ) as ThemeRoles;
  const valid = Object.values(roles).every(
    (color) => typeof color === 'string' && isValidHex(color),
  );
  if (!valid) return value;
  const normalized = Object.fromEntries(
    Object.entries(roles).map(([id, color]) => [id, normalizeHex(color)]),
  ) as ThemeRoles;
  const mode = getRelativeLuminance(normalized.background) < 0.3 ? 'dark' : 'light';
  const themes = themesFromPalette([normalized.primary, normalized.secondary, normalized.accent]);
  themes[mode] = completeTheme(normalized);
  const { roles: _roles, ...remaining } = value;
  return { ...defaults, ...remaining, mode, themes };
}

export function parsePlayground(value: unknown): PlaygroundState {
  const defaults = createDefaultPlayground();
  if (!isRecord(value)) return defaults;
  const data = migrateLegacyPlayground(value);
  const ids = new Set<string>();
  const snapshots: ThemeSnapshot[] = Array.isArray(data.snapshots)
    ? data.snapshots.slice(0, MAX_THEME_SNAPSHOTS).flatMap((item) => {
        if (
          !isRecord(item) ||
          typeof item.id !== 'string' ||
          !item.id ||
          item.id.length > 120 ||
          ids.has(item.id) ||
          !isRecord(item.design)
        )
          return [];
        ids.add(item.id);
        return [
          {
            id: item.id,
            name: text(item.name, 'Saved version', 60).trim() || 'Saved version',
            createdAt: text(item.createdAt, '', 40),
            design: parseDesign(item.design, defaults),
          },
        ];
      })
    : [];
  const source = isRecord(data.source) ? data.source : {};
  const referenceId =
    typeof data.referenceId === 'string' && snapshots.some((item) => item.id === data.referenceId)
      ? data.referenceId
      : null;
  const compare = choice(data.compare, ['none', 'modes', 'snapshot'], 'none');
  return {
    ...parseDesign(data, defaults),
    mode: choice(data.mode, ['light', 'dark'], 'light'),
    template: choice(
      data.template,
      ['interface', 'components', 'editorial', 'poster', 'custom'],
      'interface',
    ),
    vision: choice(
      data.vision,
      ['normal', 'protanopia', 'deuteranopia', 'tritanopia', 'grayscale'],
      'normal',
    ),
    viewport: choice(data.viewport, ['fit', 'desktop', 'tablet', 'mobile'], 'fit'),
    compare: compare === 'snapshot' && !referenceId ? 'none' : compare,
    referenceId,
    snapshots,
    source: {
      html: text(source.html, defaults.source.html, SOURCE_LIMIT),
      css: text(source.css, defaults.source.css, SOURCE_LIMIT),
    },
  };
}
